import React from "react";
import { BrowserRouter } from "react-router-dom";
import useIntercom from "@reclaim-ai/react-intercom-hook";
import { ToastProvider } from "./designSystem";
import { ThemeProviderContainer as Theme } from "./components/theme";
import { CompanyContextProvider } from "./components/companies/useCompanyContext";
import { AuthProvider } from "./auth/AuthProvider";
import { CheckOnboardingFlow } from "./components/CheckOnboardingFlow";
import { Router } from "./routes/Router";
import { routes } from "./routes/RoutesConfig";
import { useActivityTracker, useInactivityTimeout, useSaveOnClose } from "./components/utility/hooks/activitytracker";
import { AnalyticsProvider } from "./analytics";
import { DynamicWalletProvider } from "./wallet/dynamic/DynamicWalletProvider";
import { AvererWalletProvider } from "./wallet/averer/AvererWalletProvider";
import { isAvererWalletSelected } from "./wallet/walletProvider";
import { ReactErrorBoundary } from "./components/utility/ReactErrorBoundary";
import { errorMessage } from "./components/commons/errorHandler";

const WalletProvider = isAvererWalletSelected() ? AvererWalletProvider : DynamicWalletProvider;

const AppErrorFallback: React.FC = () => (
  <div style={{ padding: "2rem", textAlign: "center" }}>
    <h1>Something went wrong</h1>
    <p>Please refresh the page or try again later.</p>
  </div>
);

export const App: React.FC = () => {
  useIntercom({ app_id: window.config.INTERCOM_APP_ID });
  useInactivityTimeout();
  useActivityTracker();
  useSaveOnClose();

  // BrowserRouter must wrap AuthProvider to give access to react-router-dom
  return (
    <ReactErrorBoundary
      fallback={<AppErrorFallback />}
      onError={(error, info) => {
        errorMessage(error, "Unhandled application error", false, {
          operation: "ReactErrorBoundary",
          route: window.location.pathname,
        });
        if (info.componentStack) {
          console.debug(info.componentStack);
        }
      }}
    >
      <BrowserRouter>
        <AuthProvider>
          <Theme>
            <ToastProvider maxVisible={6}>
              <div className="App">
                <WalletProvider>
                  <CompanyContextProvider>
                    <AnalyticsProvider>
                      <main style={{ minHeight: "100vh" }}>
                        <CheckOnboardingFlow />
                        <Router routes={routes} />
                      </main>
                    </AnalyticsProvider>
                  </CompanyContextProvider>
                </WalletProvider>
              </div>
            </ToastProvider>
          </Theme>
        </AuthProvider>
      </BrowserRouter>
    </ReactErrorBoundary>
  );
};
